import { useState } from "react";
import BaseModal from "../BaseComponents/BaseModal/BaseModal";
import ToggleSwitch from "../BaseComponents/Toggle/ToggleSwitch";
import SearchableSelect from "../BaseComponents/SearchableSelect/SearchableSelect";
import { useGetCountries } from "../../api/geo/useCountries";
import styles from "./AddSupplierModal.module.css";

const SUPPLIER_TYPES = ["API", "Hybrid", "Offline"];

const VAT_ROLES = [
  "EU travel agency",
  "Belgian travel agency",
  "Non-EU travel agency",
  "Direct supplier"
];

const SERVICES = [
  { id: "hotels", label: "Hotels", icon: "🏨" },
  { id: "transfers", label: "Transfers", icon: "🚗" },
  { id: "externalPackage", label: "External Package Holiday", icon: "📦" },
  { id: "incoming", label: "Incoming", icon: "✈️" },
  { id: "carRental", label: "Car Rental", icon: "🚙" },
  { id: "insurance", label: "Insurance", icon: "🛡️" },
  { id: "visa", label: "Visa", icon: "📄" },
  { id: "flightService", label: "Flight Service", icon: "✈️" },
  { id: "otherServices", label: "Other Services", icon: "⭐" }
];

const HOTEL_COMMERCIAL_ROLES = [
  { key: "MERCHANT", label: "Merchant (own risk)" },
  { key: "AGENT", label: "Agent (intermediary)" },
  { key: "BOTH", label: "Both" },
];

const OTHER_COMMERCIAL_ROLES = [
  { key: "MERCHANT", label: "Merchant (own risk)" },
  { key: "AGENT", label: "Agent (intermediary)" },
];

const emptyForm = {
  supplierId: "",
  supplierCode: "",
  name: "",
  type: "API",
  status: true,
  internalNotes: "",
  services: {},
  serviceRoles: {},
  serviceDefaultRoles: {},
  country: "",
  vatRole: "",
  contactEmail: "",
  phone: "",
  contactPersonName: ""
};

const AddSupplierModal = ({ open, onClose, onSubmit, submitting = false }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const { data: countries } = useGetCountries({ limit: 300, active: true, sortBy: 'name', sortOrder: 'ASC' });

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    // Clear error when user starts typing
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: "" }));
    }
  };

  const handleBlur = (key) => {
    setTouched(prev => ({ ...prev, [key]: true }));
    validateField(key, form[key]);
  };

  const validateField = (key, value) => {
    let error = "";

    switch (key) {
      case "supplierCode":
        if (!value || !value.trim()) {
          error = "Supplier Code is required";
        } else if (value.length < 2) {
          error = "Supplier Code must be at least 2 characters";
        }
        break;
      case "name":
        if (!value || !value.trim()) {
          error = "Name is required";
        }
        break;
      case "country":
        if (!value) {
          error = "Country is required";
        }
        break;
      case "vatRole":
        if (!value) {
          error = "Supplier VAT Role is required";
        }
        break;
      case "contactEmail":
        if (!value || !value.trim()) {
          error = "Contact Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = "Please enter a valid email address";
        }
        break;
      default:
        break;
    }

    setErrors(prev => ({ ...prev, [key]: error }));
    return error;
  };

  const validateAllFields = () => {
    const newErrors = {};
    let isValid = true;

    // Validate required fields
    if (!form.supplierCode.trim()) {
      newErrors.supplierCode = "Supplier Code is required";
      isValid = false;
    } else if (form.supplierCode.length < 2) {
      newErrors.supplierCode = "Supplier Code must be at least 2 characters";
      isValid = false;
    }

    if (!form.name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }

    if (!form.type) {
      newErrors.type = "Supplier Type is required";
      isValid = false;
    }

    // Check if at least one service is enabled
    const hasEnabledService = Object.values(form.services).some(v => v === true);
    if (!hasEnabledService) {
      newErrors.services = "At least one service must be enabled";
      isValid = false;
    }

    if (!form.country) {
      newErrors.country = "Country is required";
      isValid = false;
    }

    if (!form.vatRole) {
      newErrors.vatRole = "Supplier VAT Role is required";
      isValid = false;
    }

    if (!form.contactEmail.trim()) {
      newErrors.contactEmail = "Contact Email is required";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail)) {
      newErrors.contactEmail = "Please enter a valid email address";
      isValid = false;
    }

    setErrors(newErrors);

    // Switch to the tab with the first error
    if (!isValid) {
      if (newErrors.supplierCode || newErrors.name || newErrors.type) {
        setActiveTab("overview");
      } else if (newErrors.services) {
        setActiveTab("services");
      } else if (newErrors.country || newErrors.vatRole) {
        setActiveTab("vat");
      } else if (newErrors.contactEmail) {
        setActiveTab("contact");
      }
    }

    return isValid;
  };

  const handleServiceToggle = (serviceId) => {
    setForm(prev => ({
      ...prev,
      services: {
        ...prev.services,
        [serviceId]: !prev.services[serviceId]
      }
    }));
    if (errors.services) {
      setErrors(prev => ({ ...prev, services: "" }));
    }
  };

  const handleServiceRoleChange = (serviceId, role) => {
    setForm(prev => ({
      ...prev,
      serviceRoles: { ...prev.serviceRoles, [serviceId]: role },
      // clear default role when switching away from BOTH
      serviceDefaultRoles: role !== "BOTH"
        ? { ...prev.serviceDefaultRoles, [serviceId]: undefined }
        : prev.serviceDefaultRoles,
    }));
  };

  const handleServiceDefaultRoleChange = (serviceId, defaultRole) => {
    setForm(prev => ({
      ...prev,
      serviceDefaultRoles: { ...prev.serviceDefaultRoles, [serviceId]: defaultRole }
    }));
  };

  const handleReset = () => {
    setForm(emptyForm);
    setActiveTab("overview");
    setErrors({});
    setTouched({});
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleSubmit = async () => {
    const isValid = validateAllFields();

    if (!isValid) {
      return;
    }

    const services = SERVICES.map(service => {
      const role = form.serviceRoles[service.id] || (service.id === "hotels" ? "DEFAULT" : "MERCHANT");
      return {
        serviceType: service.label,
        enabled: form.services[service.id] === true,
        commercialRole: role || "MERCHANT",
        defaultCommercialRole: role === "BOTH" ? (form.serviceDefaultRoles[service.id] || "MERCHANT") : null,
      };
    });

    const payload = {
      code: form.supplierCode.trim().toUpperCase(),
      name: form.name.trim(),
      type: form.type,
      status: form.status ? "Active" : "Inactive",
      internalNotes: form.internalNotes.trim(),
      services,
      country: form.country,
      vatRole: form.vatRole,
      contactEmail: form.contactEmail.trim(),
      contactPhone: form.phone.trim(),
      contactPersonName: form.contactPersonName.trim()
    };

    try {
      if (onSubmit) {
        await onSubmit(payload);
      }
      handleClose();
    } catch (err) {
      // Error feedback handled by parent via toast.
    }
  };
  const hasErrors = Object.keys(errors).length > 0 && Object.values(errors).some(e => e !== "");

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title="Add Supplier"
      width="900px"
      footer={
        <>
          <button className={styles.closeBtn} onClick={handleClose}>
            ✕ Close
          </button>
          <button className={styles.saveBtn} onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Saving..." : "Save Changes"}
          </button>
        </>
      }
    >
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.typeBadge}>
            {form.type}
          </div>
          <div className={styles.headerInfo}>
            <h2>{form.name || "New Supplier"}</h2>
            <p>
              ID: {form.supplierId || "AUTO"} • Code: {form.supplierCode || "—"}
            </p>
          </div>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.statusToggle}>
            <span className={form.status ? styles.activeLabel : styles.inactiveLabel}>
              {form.status ? "Active" : "Inactive"}
            </span>
          </div>
        </div>
      </div>

      {/* Validation Error Banner */}
      {hasErrors && (
        <div className={styles.errorBanner}>
          <div className={styles.errorIcon}>⚠️</div>
          <div className={styles.errorContent}>
            <strong>Please fix the following errors:</strong>
            <ul>
              {Object.entries(errors).map(([key, error]) => 
                error && <li key={key}>{error}</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === "overview" ? styles.activeTab : ""} ${
            (errors.supplierCode || errors.name || errors.type) ? styles.tabWithError : ""
          }`}
          onClick={() => setActiveTab("overview")}
        >
          ℹ️ Overview
          {(errors.supplierCode || errors.name || errors.type) && (
            <span className={styles.errorDot}></span>
          )}
        </button>
        <button
          className={`${styles.tab} ${activeTab === "services" ? styles.activeTab : ""} ${
            errors.services ? styles.tabWithError : ""
          }`}
          onClick={() => setActiveTab("services")}
        >
          📦 Product / Service Scope
          {errors.services && <span className={styles.errorDot}></span>}
        </button>
        <button
          className={`${styles.tab} ${activeTab === "vat" ? styles.activeTab : ""} ${
            (errors.country || errors.vatRole) ? styles.tabWithError : ""
          }`}
          onClick={() => setActiveTab("vat")}
        >
          🌍 VAT / Country
          {(errors.country || errors.vatRole) && <span className={styles.errorDot}></span>}
        </button>
        <button
          className={`${styles.tab} ${activeTab === "contact" ? styles.activeTab : ""} ${
            errors.contactEmail ? styles.tabWithError : ""
          }`}
          onClick={() => setActiveTab("contact")}
        >
          ✉️ Contact
          {errors.contactEmail && <span className={styles.errorDot}></span>}
        </button>
      </div>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className={styles.overviewTab}>
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>📋 Basic Information</h4>
              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label>Supplier ID</label>
                  <input
                    value={form.supplierId}
                    onChange={e => handleChange("supplierId", e.target.value.toUpperCase())}
                    placeholder="Auto-generated"
                    disabled
                    className={styles.disabledInput}
                  />
                  <span className={styles.hint}>Auto-generated by system identifier</span>
                </div>
                <div className={styles.field}>
                  <label>Supplier Code <span className={styles.required}>*</span></label>
                  <input
                    value={form.supplierCode}
                    onChange={e => handleChange("supplierCode", e.target.value.toUpperCase())}
                    onBlur={() => handleBlur("supplierCode")}
                    placeholder="AMADEUS"
                    className={`${styles.codeInput} ${errors.supplierCode ? styles.inputError : ""}`}
                  />
                  {errors.supplierCode && (
                    <span className={styles.errorText}>{errors.supplierCode}</span>
                  )}
                  {!errors.supplierCode && (
                    <span className={styles.hint}>Required, must be unique</span>
                  )}
                </div>
              </div>

              <div className={styles.fieldWide}>
                <label>Name <span className={styles.required}>*</span></label>
                <input
                  value={form.name}
                  onChange={e => handleChange("name", e.target.value)}
                  onBlur={() => handleBlur("name")}
                  placeholder="Amadeus GDS"
                  className={errors.name ? styles.inputError : ""}
                />
                {errors.name && (
                  <span className={styles.errorText}>{errors.name}</span>
                )}
              </div>

              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label>Supplier Type <span className={styles.required}>*</span></label>
                  <select
                    value={form.type}
                    onChange={e => handleChange("type", e.target.value)}
                    className={errors.type ? styles.inputError : ""}
                  >
                    {SUPPLIER_TYPES.map(type => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  {errors.type && (
                    <span className={styles.errorText}>{errors.type}</span>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Status</label>
                  <div className={styles.toggleField}>
                    <span>{form.status ? "Active" : "Inactive"}</span>
                    <ToggleSwitch
                      checked={form.status}
                      onChange={() => handleChange("status", !form.status)}
                    />
                  </div>
                </div>
              </div>

              <div className={styles.fieldWide}>
                <label>Internal Notes</label>
                <textarea
                  value={form.internalNotes}
                  onChange={e => handleChange("internalNotes", e.target.value)}
                  placeholder="Initial integration completed on Jan 15, 2026. Primary contact: Tech Support Team."
                  rows={4}
                  className={styles.textarea}
                />
              </div>
            </div>

            {/* Audit Information */}
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>📊 Audit Information</h4>
              <div className={styles.auditGrid}>
                <div className={styles.auditItem}>
                  <span className={styles.auditLabel}>CREATED AT</span>
                  <div className={styles.auditValue}>—</div>
                  <div className={styles.auditBy}>By: System Admin</div>
                </div>
                <div className={styles.auditItem}>
                  <span className={styles.auditLabel}>UPDATED AT</span>
                  <div className={styles.auditValue}>—</div>
                  <div className={styles.auditBy}>By: —</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Services Tab */}
        {activeTab === "services" && (
          <div className={styles.servicesTab}>
            <div className={styles.servicesGrid}>
              {SERVICES.map(service => {
                const isEnabled = form.services[service.id] === true;
                const isHotel = service.id === "hotels";
                const roleOptions = isHotel ? HOTEL_COMMERCIAL_ROLES : OTHER_COMMERCIAL_ROLES;
                const currentRole = form.serviceRoles[service.id] || "MERCHANT";
                return (
                  <div
                    key={service.id}
                    className={`${styles.serviceCard} ${isEnabled ? styles.serviceEnabled : ""}`}
                  >
                    <div className={styles.serviceCardTop}>
                      <div className={styles.serviceIcon}>{service.icon}</div>
                      <div className={styles.serviceInfo}>
                        <div className={styles.serviceName}>{service.label}</div>
                        <div className={styles.serviceStatus}>
                          {isEnabled ? "Enabled" : "Disabled"}
                        </div>
                      </div>
                      <ToggleSwitch
                        checked={isEnabled}
                        onChange={() => handleServiceToggle(service.id)}
                      />
                    </div>
                    {isEnabled && (
                      <div className={styles.serviceCardRole}>
                        <span className={styles.serviceCardRoleLabel}>Commercial Role</span>
                        <select
                          className={styles.serviceCardRoleSelect}
                          value={currentRole}
                          onChange={(e) => handleServiceRoleChange(service.id, e.target.value)}
                        >
                          {roleOptions.map(r => (
                            <option key={r.key} value={r.key}>{r.label}</option>
                          ))}
                        </select>
                        {isHotel && currentRole === "BOTH" && (
                          <div style={{ marginTop: 8 }}>
                            <span className={styles.serviceCardRoleLabel}>Default Role in Booking</span>
                            <select
                              className={styles.serviceCardRoleSelect}
                              value={form.serviceDefaultRoles[service.id] || "MERCHANT"}
                              onChange={(e) => handleServiceDefaultRoleChange(service.id, e.target.value)}
                            >
                              <option value="MERCHANT">Merchant (own risk)</option>
                              <option value="AGENT">Agent (intermediary)</option>
                            </select>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {errors.services && (
              <div className={styles.fieldError}>
                <span className={styles.errorIcon}>⚠️</span>
                <span className={styles.errorText}>{errors.services}</span>
              </div>
            )}

            <div className={styles.infoBox}>
              <div className={styles.infoIcon}>ℹ️</div>
              <div className={styles.infoContent}>
                <strong>Service Scope Rules</strong>
                <ul>
                  <li>At least one service must be enabled</li>
                  <li>Multiple selections are allowed</li>
                  <li>Changes will affect supplier capabilities immediately</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* VAT Tab */}
        {activeTab === "vat" && (
          <div className={styles.vatTab}>
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>🌍 VAT & Country Configuration</h4>
              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label>Country <span className={styles.required}>*</span></label>
                  <SearchableSelect
                    value={form.country}
                    onChange={(val) => handleChange("country", val)}
                    onBlur={() => handleBlur("country")}
                    options={countries.map(c => ({ value: c.name, label: c.name }))}
                    placeholder="Select country"
                    searchPlaceholder="Search countries..."
                    error={!!errors.country}
                  />
                  {errors.country && (
                    <span className={styles.errorText}>{errors.country}</span>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Supplier VAT Role <span className={styles.required}>*</span></label>
                  <select
                    value={form.vatRole}
                    onChange={e => handleChange("vatRole", e.target.value)}
                    onBlur={() => handleBlur("vatRole")}
                    className={errors.vatRole ? styles.inputError : ""}
                  >
                    <option value="">Select VAT role</option>
                    {VAT_ROLES.map(role => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                  {errors.vatRole && (
                    <span className={styles.errorText}>{errors.vatRole}</span>
                  )}
                </div>
              </div>

              {form.country && (
                <div className={styles.euBadge}>
                  <span className={styles.badgeLabel}>EU Member State</span>
                  <span className={styles.badgeText}>
                    Automatically determined based on selected country
                  </span>
                </div>
              )}
            </div>

            <div className={styles.warningBox}>
              <div className={styles.warningIcon}>⚠️</div>
              <div className={styles.warningContent}>
                <strong>VAT Configuration Notes</strong>
                <ul>
                  <li>EU/Non-EU status is derived automatically from country selection</li>
                  <li>No manual EU toggle is available</li>
                  <li>VAT role affects invoice and tax calculations</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Contact Tab */}
        {activeTab === "contact" && (
          <div className={styles.contactTab}>
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>✉️ Contact Information</h4>
              
              <div className={styles.fieldWide}>
                <label>Contact Email <span className={styles.required}>*</span></label>
                <div className={styles.inputWithIcon}>
                  <span className={styles.inputIcon}>📧</span>
                  <input
                    type="email"
                    value={form.contactEmail}
                    onChange={e => handleChange("contactEmail", e.target.value)}
                    onBlur={() => handleBlur("contactEmail")}
                    placeholder="support@amadeus.com"
                    className={errors.contactEmail ? styles.inputError : ""}
                  />
                </div>
                {errors.contactEmail && (
                  <span className={styles.errorText}>{errors.contactEmail}</span>
                )}
              </div>

              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label>Phone</label>
                  <div className={styles.inputWithIcon}>
                    <span className={styles.inputIcon}>📞</span>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={e => handleChange("phone", e.target.value)}
                      placeholder="+33 1 23 45 67 89"
                    />
                  </div>
                </div>
                <div className={styles.field}>
                  <label>Contact Person Name</label>
                  <div className={styles.inputWithIcon}>
                    <span className={styles.inputIcon}>👤</span>
                    <input
                      value={form.contactPersonName}
                      onChange={e => handleChange("contactPersonName", e.target.value)}
                      placeholder="Jean Dupont"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.infoBox}>
              <div className={styles.infoIcon}>👤</div>
              <div className={styles.infoContent}>
                <strong>Contact Information Usage</strong>
                <p>
                  This information is used for operational communications, technical support
                  requests, and business correspondence.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </BaseModal>
  );
};

export default AddSupplierModal;

